/**
 * TASK-231: Client Profile Form Mapper
 *
 * Maps between ClientComplianceData and ClientProfileData.
 * Used for custodian bank account opening forms.
 */

import type { ClientComplianceData, DataSource, TrackedField } from '../../../types/clientCompliance';
import { unwrap, createTrackedField, createDataSource } from '../../../types/clientCompliance';
import { transformDate } from '../transformers/dateTransformer';
import type { ClientProfileData, MapperOptions } from '../types';
import { joinNames } from './kycMapper';

/**
 * Map ClientComplianceData to ClientProfileData
 *
 * @param data - Shared compliance data
 * @param options - Mapper options
 * @returns Partial Client Profile data
 */
export function mapToClientProfile(
  data: ClientComplianceData,
  _options?: MapperOptions
): Partial<ClientProfileData> {
  const personal = data.personal || {};
  const contact = data.contact || {};
  const tax = data.tax || {};

  const firstName = unwrap(personal.firstName) || '';
  const lastName = unwrap(personal.lastName) || '';
  const fullName = joinNames(firstName, lastName);

  const nationalities = unwrap(personal.nationalities) || [];

  return {
    accountHolder: {
      name: fullName || undefined,
      dateOfBirth: unwrap(personal.dateOfBirth),
      nationality: nationalities[0],
      address: unwrap(contact.residentialAddress),
      phone: unwrap(contact.phonePrimary),
      email: unwrap(contact.emailPrimary),
    },
    taxResidence: unwrap(tax.taxResidence),
    usPerson: unwrap(tax.usPerson),
    beneficialOwner: {
      // Default to self if no beneficial owners defined
      type: data.beneficialOwners && data.beneficialOwners.length > 0 ? 'third_party' : 'self',
      percentage: 100,
      name: fullName || undefined,
      dateOfBirth: unwrap(personal.dateOfBirth),
      nationality: nationalities[0],
    },
  };
}

/**
 * Map ClientProfileData back to ClientComplianceData
 *
 * @param formData - Client Profile form data
 * @param source - Data source for tracking
 * @returns Partial shared compliance data
 */
export function mapFromClientProfile(
  formData: Partial<ClientProfileData>,
  source: DataSource
): Partial<ClientComplianceData> {
  const accountHolder = formData.accountHolder;
  const beneficialOwner = formData.beneficialOwner;

  const createField = <T>(value: T | undefined): TrackedField<T> | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    return createTrackedField(value as T, source);
  };

  // Parse full name into first/last
  let firstName: string | undefined;
  let lastName: string | undefined;

  if (accountHolder?.name) {
    const nameParts = accountHolder.name.trim().split(/\s+/);
    firstName = nameParts[0];
    lastName = nameParts.slice(1).join(' ') || undefined;
  }

  const result: Partial<ClientComplianceData> = {
    personal: {
      firstName: createField(firstName),
      lastName: createField(lastName),
      dateOfBirth: createField(accountHolder?.dateOfBirth ? transformDate(accountHolder.dateOfBirth) : undefined),
      nationalities: accountHolder?.nationality ? createField([accountHolder.nationality]) : undefined,
    },
    contact: {
      phonePrimary: createField(accountHolder?.phone),
      emailPrimary: createField(accountHolder?.email),
      residentialAddress: createField(accountHolder?.address),
    },
    tax: {
      taxResidence: createField(formData.taxResidence),
      usPerson: createField(formData.usPerson),
    },
  };

  // Handle beneficial owner if it's a third party
  if (beneficialOwner?.type === 'third_party' && beneficialOwner.name) {
    result.beneficialOwners = [
      {
        ownerName: createField(beneficialOwner.name),
        ownerDob: createField(beneficialOwner.dateOfBirth ? transformDate(beneficialOwner.dateOfBirth) : undefined),
        ownerNationality: createField(beneficialOwner.nationality),
        ownerPercentage: createField(beneficialOwner.percentage),
        controlType: createField('direct'),
      },
    ];
  }

  return result;
}

/**
 * Create a Client Profile data source
 */
export function createClientProfileSource(clientId: string, userId?: string): DataSource {
  return createDataSource(
    'manual',
    `clientprofile_${clientId}_${Date.now()}`,
    'Client Profile',
    1.0,
    undefined,
    userId
  );
}
